'use client'

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

import useEmblaCarousel from 'embla-carousel-react'
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu'

import { shouldReduceMotion } from '@/core/config/motion'
import { cn } from '@/core/utils/common'

import type { ReactNode } from 'react'

type CarouselSlide = {
  id: string
  content: ReactNode
}

type CarouselProps = {
  slides: CarouselSlide[]
  className?: string
  showArrows?: boolean
  showDots?: boolean
  ariaLabel?: string
  loop?: boolean
  autoplayMs?: number
  classNames?: {
    slide?: string
    viewport?: string
    arrow?: string
    arrowPrev?: string
    arrowNext?: string
    dots?: string
  }
}

export default function Carousel({
  slides,
  className,
  showArrows = true,
  showDots = true,
  ariaLabel = '슬라이드',
  loop = false,
  autoplayMs,
  classNames,
}: CarouselProps) {
  const total = slides.length
  const [viewportRef, emblaApi] = useEmblaCarousel({
    active: total > 0,
    align: 'start',
    containScroll: 'trimSnaps',
    loop,
    watchDrag: total > 1,
  })
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!emblaApi) return () => {}
      emblaApi.on('select', onChange)
      emblaApi.on('reInit', onChange)
      return () => {
        emblaApi.off('select', onChange)
        emblaApi.off('reInit', onChange)
      }
    },
    [emblaApi]
  )
  const getSnapshot = useCallback(
    () => emblaApi?.selectedScrollSnap() ?? 0,
    [emblaApi]
  )
  const current = useSyncExternalStore(subscribe, getSnapshot, () => 0)
  const canPrevious = useSyncExternalStore(
    subscribe,
    useCallback(() => emblaApi?.canScrollPrev() ?? loop, [emblaApi, loop]),
    () => loop
  )
  const canNext = useSyncExternalStore(
    subscribe,
    useCallback(
      () => emblaApi?.canScrollNext() ?? total > 1,
      [emblaApi, total]
    ),
    () => total > 1
  )
  const pausedRef = useRef(false)

  useEffect(() => {
    if (!emblaApi || !autoplayMs || total < 2 || shouldReduceMotion()) return
    const timer = window.setInterval(() => {
      if (!pausedRef.current && document.visibilityState === 'visible') {
        emblaApi.scrollNext()
      }
    }, autoplayMs)
    return () => window.clearInterval(timer)
  }, [emblaApi, autoplayMs, total])

  const scrollPrevious = () => {
    emblaApi?.scrollPrev(shouldReduceMotion())
  }

  const scrollNext = () => {
    emblaApi?.scrollNext(shouldReduceMotion())
  }

  const scrollTo = (index: number) => {
    emblaApi?.scrollTo(index, shouldReduceMotion())
  }

  return (
    <section
      aria-label={ariaLabel}
      aria-roledescription="캐러셀"
      className={cn(className)}
      onPointerEnter={() => {
        pausedRef.current = true
      }}
      onPointerLeave={() => {
        pausedRef.current = false
      }}
      onFocusCapture={() => {
        pausedRef.current = true
      }}
      onBlurCapture={() => {
        pausedRef.current = false
      }}
    >
      <div data-carousel-frame className="relative">
        <div
          ref={viewportRef}
          className={cn('overflow-hidden', classNames?.viewport)}
        >
          <div className="flex touch-pan-y">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                role="group"
                aria-label={`${index + 1} / ${total}`}
                aria-roledescription="슬라이드"
                className={cn(
                  'min-w-0',
                  classNames?.slide ?? 'flex-[0_0_100%]'
                )}
              >
                {slide.content}
              </div>
            ))}
          </div>
        </div>

        {showArrows && total > 1 && (
          <>
            <button
              type="button"
              onClick={scrollPrevious}
              disabled={!canPrevious}
              className={cn(
                'absolute flex size-11 items-center justify-center',
                classNames?.arrowPrev ?? 'left-2 top-1/2 -translate-y-1/2',
                'cursor-pointer rounded-control bg-background/80 transition-colors hover:bg-background',
                'focus:outline-none disabled:cursor-not-allowed disabled:opacity-30',
                classNames?.arrow
              )}
              aria-label="이전"
            >
              <LuChevronLeft aria-hidden="true" className="size-5" />
            </button>
            <button
              type="button"
              onClick={scrollNext}
              disabled={!canNext}
              className={cn(
                'absolute flex size-11 items-center justify-center',
                classNames?.arrowNext ?? 'right-2 top-1/2 -translate-y-1/2',
                'cursor-pointer rounded-control bg-background/80 transition-colors hover:bg-background',
                'focus:outline-none disabled:cursor-not-allowed disabled:opacity-30',
                classNames?.arrow
              )}
              aria-label="다음"
            >
              <LuChevronRight aria-hidden="true" className="size-5" />
            </button>
          </>
        )}
      </div>

      {showDots && total > 1 && (
        <div className={cn('mt-4 flex justify-center', classNames?.dots)}>
          {slides.map((slide, index) => (
            <button
              key={`dot-${slide.id}`}
              type="button"
              onClick={() => scrollTo(index)}
              aria-current={index === current ? 'true' : undefined}
              aria-label={`슬라이드 ${index + 1}`}
              className="flex size-8 cursor-pointer items-center justify-center rounded-full focus:outline-none"
            >
              <span
                aria-hidden="true"
                className={cn(
                  'size-2 rounded-full transition-colors',
                  index === current ? 'bg-foreground' : 'bg-foreground/20'
                )}
              />
            </button>
          ))}
        </div>
      )}

      {total > 0 && (
        <p aria-live="polite" aria-atomic="true" className="sr-only">
          슬라이드 {current + 1} / {total}
        </p>
      )}
    </section>
  )
}
